import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChartLine, Download, Printer, ArrowDown, ArrowUp, ArrowsClockwise } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(ym) {
  if (!ym || ym.length < 7) return ym || '—';
  const [y, m] = ym.split('-');
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function firstOfThisYearIso() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

export default function ReportsPage({ user }) {
  const [dateFrom, setDateFrom] = useState(firstOfThisYearIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState({ months: [], totals: { in_pallets: 0, out_pallets: 0, net_pallets: 0, in_count: 0, out_count: 0 } });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/reports/monthly-in-out`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: from, date_to: to },
      });
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => fetchReport();

  const handleDownloadCsv = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const res = await axios.get(`${API}/reports/monthly-in-out.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monthly_in_out_${dateFrom}_${dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handlePrint = () => {
    const now = new Date().toLocaleString();
    const rows = data.months.map((m, i) => `
      <tr>
        <td class="idx">${i + 1}</td>
        <td class="mono bold">${formatMonth(m.month)}</td>
        <td class="mono in">${m.in_pallets}</td>
        <td class="mono">${m.in_count}</td>
        <td class="mono out">${m.out_pallets}</td>
        <td class="mono">${m.out_count}</td>
        <td class="mono ${m.net_pallets >= 0 ? 'in' : 'out'}">${m.net_pallets >= 0 ? '+' : ''}${m.net_pallets}</td>
      </tr>
    `).join('');
    const totals = data.totals || {};
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Monthly IN/OUT Report</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          * { box-sizing: border-box; }
          body { font-family: Helvetica, Arial, sans-serif; margin: 0; padding: 16px; font-size: 11px; color: #000; }
          .header { border-bottom: 3px solid #000; padding-bottom: 12px; margin-bottom: 14px; display: flex; justify-content: space-between; align-items: flex-start; }
          h1 { font-size: 22px; font-weight: 900; margin: 0; letter-spacing: 1px; }
          .sub { font-size: 10px; color: #444; margin-top: 4px; }
          .range { border: 2px solid #000; padding: 6px 12px; text-align: right; }
          .range .label { font-size: 8px; letter-spacing: 2px; color: #555; }
          .range .val { font-size: 12px; font-weight: 700; margin-top: 2px; }
          .summary { display: flex; gap: 20px; padding: 10px 12px; background: #f8f8f8; border-left: 4px solid #000; margin-bottom: 12px; font-size: 11px; }
          .summary strong { font-size: 15px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { padding: 8px 10px; border: 1px solid #666; text-align: left; }
          th { background: #000; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; }
          .idx { text-align: center; width: 30px; }
          .mono { font-family: Courier New, monospace; }
          .bold { font-weight: 700; }
          .in { color: #16A34A; font-weight: 700; }
          .out { color: #DC2626; font-weight: 700; }
          tfoot td { background: #f0f0f0; font-weight: 900; }
          .footer { margin-top: 20px; display: flex; justify-content: space-between; font-size: 8px; color: #666; border-top: 1px solid #ccc; padding-top: 6px; }
          @media print { body { padding: 0; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>MONTHLY IN / OUT REPORT</h1>
            <div class="sub">Warehouse Management System • Printed: ${now}</div>
          </div>
          <div class="range">
            <div class="label">DATE RANGE</div>
            <div class="val">${dateFrom} → ${dateTo}</div>
          </div>
        </div>

        <div class="summary">
          <span>Months: <strong>${data.months.length}</strong></span>
          <span>Total IN: <strong class="in">${totals.in_pallets || 0} plt</strong></span>
          <span>Total OUT: <strong class="out">${totals.out_pallets || 0} plt</strong></span>
          <span>Net: <strong>${(totals.net_pallets || 0) >= 0 ? '+' : ''}${totals.net_pallets || 0} plt</strong></span>
        </div>

        <table>
          <thead>
            <tr>
              <th class="idx">#</th>
              <th>Month</th>
              <th>IN Pallets</th>
              <th>IN Count</th>
              <th>OUT Pallets</th>
              <th>OUT Count</th>
              <th>Net</th>
            </tr>
          </thead>
          <tbody>${rows || '<tr><td colspan="7" style="text-align:center;padding:20px;color:#666">No movements in this date range</td></tr>'}</tbody>
          <tfoot>
            <tr>
              <td colspan="2">TOTAL</td>
              <td class="in mono">${totals.in_pallets || 0}</td>
              <td class="mono">${totals.in_count || 0}</td>
              <td class="out mono">${totals.out_pallets || 0}</td>
              <td class="mono">${totals.out_count || 0}</td>
              <td class="mono">${(totals.net_pallets || 0) >= 0 ? '+' : ''}${totals.net_pallets || 0}</td>
            </tr>
          </tfoot>
        </table>

        <div class="footer">
          <span>Monthly IN/OUT Report</span>
          <span>WMS © ${new Date().getFullYear()}</span>
        </div>

        <div class="no-print" style="margin-top: 16px; text-align:center;">
          <button onclick="window.print()" style="padding: 10px 24px; font-size: 14px; cursor: pointer;">Print Now</button>
          <button onclick="window.close()" style="padding: 10px 24px; font-size: 14px; margin-left: 10px; cursor: pointer;">Close</button>
        </div>
      </body>
      </html>
    `;
    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) { toast.error('Please allow pop-ups to print the report'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const totals = data.totals || {};
  const maxPallets = Math.max(1, ...data.months.map(m => Math.max(m.in_pallets, m.out_pallets)));

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="reports-title">
            Monthly IN / OUT Report
          </h1>
          <p className="text-[#52525B]">Track pallet movements in and out of the warehouse over any date range</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="report-from">From</Label>
            <Input
              id="report-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mono"
              data-testid="report-date-from"
            />
          </div>
          <div>
            <Label htmlFor="report-to">To</Label>
            <Input
              id="report-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mono"
              data-testid="report-date-to"
            />
          </div>
          <button
            onClick={handleApply}
            disabled={loading}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="report-apply-button"
          >
            <ArrowsClockwise size={18} weight="bold" />
            {loading ? 'Loading…' : 'Apply'}
          </button>
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleDownloadCsv}
              className="btn-secondary flex items-center gap-2"
              data-testid="report-download-csv-button"
            >
              <Download size={18} weight="bold" />
              Download CSV
            </button>
            <button
              onClick={handlePrint}
              className="btn-secondary flex items-center gap-2"
              data-testid="report-print-button"
            >
              <Printer size={18} weight="bold" />
              Print A4 PDF
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <div className="flex items-center gap-3">
            <ArrowDown size={24} className="text-[#16A34A]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Total IN</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="totals-in">{totals.in_pallets || 0}<span className="text-sm text-[#52525B] font-normal ml-1">plt</span></p>
            </div>
          </div>
        </div>
        <div className="card bg-[#DC2626]/5 border-[#DC2626]/20">
          <div className="flex items-center gap-3">
            <ArrowUp size={24} className="text-[#DC2626]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Total OUT</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="totals-out">{totals.out_pallets || 0}<span className="text-sm text-[#52525B] font-normal ml-1">plt</span></p>
            </div>
          </div>
        </div>
        <div className="card bg-[#007AFF]/5 border-[#007AFF]/20">
          <div className="flex items-center gap-3">
            <ChartLine size={24} className="text-[#007AFF]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Net Movement</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="totals-net">
                {(totals.net_pallets || 0) >= 0 ? '+' : ''}{totals.net_pallets || 0}<span className="text-sm text-[#52525B] font-normal ml-1">plt</span>
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <ChartLine size={24} className="text-[#52525B]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Months in range</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="totals-months">{data.months.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Monthly Breakdown</h2>
        {data.months.length === 0 ? (
          <div className="py-12 text-center text-[#52525B]" data-testid="report-empty">
            No completed put-away or picking movements in this date range.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="bg-[#F4F4F5] text-xs uppercase text-[#52525B]">
                <tr>
                  <th className="text-left p-3 font-medium">Month</th>
                  <th className="text-right p-3 font-medium">IN Pallets</th>
                  <th className="text-right p-3 font-medium">IN Count</th>
                  <th className="text-right p-3 font-medium">OUT Pallets</th>
                  <th className="text-right p-3 font-medium">OUT Count</th>
                  <th className="text-right p-3 font-medium">Net</th>
                  <th className="p-3 font-medium min-w-[220px]">Distribution</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m, idx) => {
                  const inPct = (m.in_pallets / maxPallets) * 100;
                  const outPct = (m.out_pallets / maxPallets) * 100;
                  return (
                    <tr key={m.month} className="border-t border-[#E4E4E7] hover:bg-[#F9FAFB]" data-testid={`report-row-${idx}`}>
                      <td className="p-3 mono font-bold text-[#09090B]">{formatMonth(m.month)}</td>
                      <td className="p-3 mono text-right font-bold text-[#16A34A]">{m.in_pallets}</td>
                      <td className="p-3 mono text-right text-[#52525B]">{m.in_count}</td>
                      <td className="p-3 mono text-right font-bold text-[#DC2626]">{m.out_pallets}</td>
                      <td className="p-3 mono text-right text-[#52525B]">{m.out_count}</td>
                      <td className={`p-3 mono text-right font-bold ${m.net_pallets >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                        {m.net_pallets >= 0 ? '+' : ''}{m.net_pallets}
                      </td>
                      <td className="p-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 flex-shrink-0"><ArrowDown size={14} weight="bold" className="text-[#16A34A]" /></div>
                            <div className="flex-1 bg-[#E4E4E7] rounded-full h-2 overflow-hidden">
                              <div className="h-full bg-[#16A34A]" style={{ width: `${inPct}%` }}></div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 flex-shrink-0"><ArrowUp size={14} weight="bold" className="text-[#DC2626]" /></div>
                            <div className="flex-1 bg-[#E4E4E7] rounded-full h-2 overflow-hidden">
                              <div className="h-full bg-[#DC2626]" style={{ width: `${outPct}%` }}></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#09090B] bg-[#F4F4F5]">
                  <td className="p-3 font-black text-[#09090B]">TOTAL</td>
                  <td className="p-3 mono text-right font-black text-[#16A34A]">{totals.in_pallets || 0}</td>
                  <td className="p-3 mono text-right text-[#52525B]">{totals.in_count || 0}</td>
                  <td className="p-3 mono text-right font-black text-[#DC2626]">{totals.out_pallets || 0}</td>
                  <td className="p-3 mono text-right text-[#52525B]">{totals.out_count || 0}</td>
                  <td className={`p-3 mono text-right font-black ${(totals.net_pallets || 0) >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>
                    {(totals.net_pallets || 0) >= 0 ? '+' : ''}{totals.net_pallets || 0}
                  </td>
                  <td className="p-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
